export const brandStrategy = {
  seo: {
    title: "Brand & strategy — Bespire AI assistant",
    description:
      "Fresh brand identity, persona refinement, business plan, competitor analysis. Your Bespire AI keeps sharpening the brand every week.",
  },
  hero: {
    badge: "Brand & strategy",
    title: "A brand that actually sounds like you",
    subtitle:
      "Not a template workshop, not a 40-page PDF. Bespire AI keeps refining your identity, messaging and positioning — using your real bookings, customers and what your competitors are doing.",
    ctaTrial: "Try it free for 3 days",
    ctaPricing: "See pricing →",
    brandCard: {
      label: "Brand card · preview",
      businessName: "Bella Studio",
      tagline: "Prenatal yoga that actually calms you down.",
      paletteLabel: "Palette",
      palette: [
        { name: "Rose", hex: "#F5B5C6" },
        { name: "Sage", hex: "#B8CBB5" },
        { name: "Sand", hex: "#EFE3D0" },
        { name: "Ink", hex: "#2B2530" },
      ],
      typographyLabel: "Typography",
      heading: "Playfair Display",
      body: "Manrope",
      voiceLabel: "Voice",
      voice: ["Warm", "Calm", "Encouraging"],
    },
  },
  process: {
    title: "Three steps so your brand works on its own",
    subtitle:
      "Not a one-off project. Every step stays live — as your data changes, Bespire refines the answers with it.",
    steps: [
      {
        step: "01",
        icon: "Target",
        title: "Positioning",
        description:
          "Who you are, who you're for, what makes you different. Bespire pulls the answers from your real customer and competitor data — not a template questionnaire.",
        bullets: [
          "Positioning map for 3–5 real competitors",
          "Segmentation of your own customers by booking history",
          "One-sentence positioning that carries into your marketing",
        ],
      },
      {
        step: "02",
        icon: "Palette",
        title: "Identity",
        description:
          "Colors, typography, voice, visual system — tuned to your personality, your main segment and your industry.",
        bullets: [
          "3 palette variations for A/B testing",
          "Type pairing and micro-system",
          "Voice guide with real examples, not principles",
        ],
      },
      {
        step: "03",
        icon: "MessageCircle",
        title: "Messaging",
        description:
          "One sentence that drives your entire marketing. From there it breaks down into website copy, posts and ads — consistent everywhere.",
        bullets: [
          "Core message + 3 variants for different segments",
          "Ready-made sentence blocks for posts",
          "Brand book that stays valid for every new piece",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "Before / after — same business",
    subtitle:
      "It's not prettier copy. It's a different position, a different audience, a different promise — from the same person.",
    before: {
      label: "Before",
      businessName: "Kata Massage",
      tagline: "Massage in Budapest. Book by phone.",
      bio: "With many years of experience I welcome my dear guests with different massage types. Booking by phone.",
      problems: [
        "No clue who the target audience is",
        "Nothing differentiates from others",
        "The voice is impersonal",
      ],
    },
    after: {
      label: "After",
      businessName: "Kata — Recovery for overworked parents",
      tagline: "45 minutes of silence so you can survive the day.",
      bio: "I work specifically with working parents. Not wellness — recovery: little talk, lots of silence, late afternoon slots. 30% off your first visit.",
      wins: [
        "Clear audience (working parents)",
        "Clear promise (recovery, not wellness)",
        "Clear differentiator (silence + timing)",
      ],
    },
  },
  gallery: {
    title: "Don't just talk about it — see it",
    subtitle:
      "Bespire generates palette, typography and voice live — you pick, it fits everything to it.",
    styles: [
      {
        name: "Calm studio",
        heading: "Playfair",
        body: "Manrope",
        tone: "Warm · Quiet · Premium",
        palette: ["#F5B5C6", "#B8CBB5", "#EFE3D0", "#2B2530"],
      },
      {
        name: "Modern clinic",
        heading: "Inter",
        body: "Inter",
        tone: "Clean · Expert · Trusted",
        palette: ["#0EA5E9", "#F1F5F9", "#0F172A", "#22D3EE"],
      },
      {
        name: "High-energy fitness",
        heading: "Space Grotesk",
        body: "Manrope",
        tone: "Bold · Direct · Motivating",
        palette: ["#F97316", "#111827", "#F3F4F6", "#FDBA74"],
      },
      {
        name: "Natural bio",
        heading: "Fraunces",
        body: "Nunito",
        tone: "Honest · Warm · Handcrafted",
        palette: ["#65A30D", "#F5F5DC", "#3F3F46", "#FDE68A"],
      },
      {
        name: "Creative studio",
        heading: "Recoleta",
        body: "Manrope",
        tone: "Playful · Bold · Unique",
        palette: ["#8B5CF6", "#F472B6", "#FEF3C7", "#1F2937"],
      },
      {
        name: "Elegant coaching",
        heading: "Cormorant",
        body: "Inter",
        tone: "Quiet · Refined · Trust-first",
        palette: ["#334155", "#E2E8F0", "#C7A17A", "#0F172A"],
      },
    ],
  },
  chat: {
    title: "Ask it like you'd ask a strategist",
    subtitle: "Just not for €500 once — any time of the day, based on real data.",
    chat: {
      title: "Brand assistant",
      online: "Online",
      messages: [
        { role: "user" as const, content: "I'd like to refresh my brand. How much would a brand refresh cost with you?" },
        { role: "assistant" as const, content: "It's already in your plan. Let's look together: your data shows 3 new customer segments in the last 3 months — which one should we position for?" },
        { role: "user" as const, content: "Working parents, 30–45, are my main audience." },
        { role: "assistant" as const, content: "Got it. I built 3 moodboards for that segment:", list: [
          "🎨 Calm studio — quiet premium, evening slots",
          "🌿 Natural bio — family focus, weekend packages",
          "✨ Creative studio — community, workshop-based",
        ] },
        { role: "user" as const, content: "Expand the first one." },
        { role: "assistant" as const, content: "Done: palette, typography, 5 posts, new landing copy, voice guide. Approve it and I'll ship it to your site today. 🚀" },
      ],
    },
  },
  crossLinks: {
    title: "Your brand doesn't stop here",
    subtitle: "Bespire uses the same identity on your website, in marketing, in customer messages and in your daily rhythm.",
  },
  cta: {
    title: "Start refining your brand today",
    subtitle: "3-day free trial, no card required.",
    primary: "Try it free for 3 days",
    secondary: "See pricing",
  },
};
